import { Component, OnInit } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';

// Angular Material
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeleteDialogComponent } from '../delete/delete-dialog.component';
import { OrganisationDialogComponent } from './organisation-dialog.component';

interface OrganisationType {
  id: number;
  name: string;
  slug: string;
}

interface Organisation {
  id: string;
  name: string;
  typeId?: number | null;
  type?: OrganisationType | null;
  allunitedSection?: string | null;
  syncStatus?: string;
  syncMessage?: string | null;
  lastSyncAt?: string | null;
  [key: string]: any;
}

@Component({
  selector: 'app-organisations',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
  ],
  templateUrl: './organisations.component.html',
  styleUrls: ['./organisations.component.scss'],
})
export class OrganisationsComponent implements OnInit {
  organisations: Organisation[] = [];
  loading = false;
  error: string | null = null;

  displayedColumns: string[] = ['id', 'name', 'type', 'actions'];

  private apiBaseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.fetchOrganisations();
  }

  fetchOrganisations() {
    this.loading = true;
    this.http
      .get<Organisation[]>(`${this.apiBaseUrl}/organizations`)
      .subscribe({
        next: (data) => {
          this.organisations = data;
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load organisations', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(OrganisationDialogComponent, {
      width: '400px',
      data: { name: '' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.createOrganisation(result);
      }
    });
  }

  openEditDialog(org: Organisation) {
    const dialogRef = this.dialog.open(OrganisationDialogComponent, {
      width: '400px',
      data: { ...org },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.updateOrganisation(org, result);
      }
    });
  }

  createOrganisation(newOrg: Partial<Organisation>) {
    this.http
      .post<Organisation>(`${this.apiBaseUrl}/organizations`, newOrg)
      .subscribe({
        next: (createdOrg) => {
          this.snackBar.open('Organisation created', 'Close', {
            duration: 2000,
          });
          // Directly switch to the new organisation
          if (createdOrg && createdOrg.id) {
            localStorage.setItem('selectedOrganisationId', createdOrg.id);
            // Optionally, reload the page to update all components
            window.location.reload();
          } else {
            this.fetchOrganisations();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to create organisation', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateOrganisation(org: Organisation, updatedData: Partial<Organisation>) {
    this.http
      .put<Organisation>(
        `${this.apiBaseUrl}/organizations/${org.id}`,
        updatedData
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Organisation updated', 'Close', {
            duration: 2000,
          });
          this.fetchOrganisations();
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to update organisation', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  deleteOrganisation(org: Organisation) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Organisation',
        message: `Are you sure you want to delete organisation "${org.name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(`${this.apiBaseUrl}/organizations/${org.id}`)
          .subscribe({
            next: () => {
              this.snackBar.open('Organisation deleted', 'Close', {
                duration: 2000,
              });
              window.location.reload();
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open('Failed to delete organisation', 'Close', {
                duration: 3000,
              });
            },
          });
      }
    });
  }

  syncOrganisation(org: Organisation) {
    // Show initial message
    this.snackBar.open('Starting sync...', 'Close', {
      duration: 2000,
    });

    // Update local state to show syncing immediately
    org.syncStatus = 'syncing';

    // Start sync
    this.http
      .post(`${this.apiBaseUrl}/organizations/${org.id}/sync/allunited`, {})
      .subscribe({
        next: () => {
          // Poll for completion
          this.pollSyncStatus(org);
        },
        error: (err: HttpErrorResponse) => {
          org.syncStatus = 'error';
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to start sync', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  private pollSyncStatus(org: Organisation, attempts: number = 0) {
    const maxAttempts = 20; // 20 attempts * 500ms = 10 seconds max

    if (attempts >= maxAttempts) {
      this.snackBar.open('Sync is taking longer than expected', 'Close', {
        duration: 3000,
      });
      this.fetchOrganisations(); // Refresh to get final status
      return;
    }

    setTimeout(() => {
      this.http
        .get<{ status: string; message?: string; lastSyncAt?: string }>(
          `${this.apiBaseUrl}/organizations/${org.id}/sync/allunited/status`
        )
        .subscribe({
          next: (statusData) => {
            org.syncStatus = statusData.status;
            org.syncMessage = statusData.message || null;
            org.lastSyncAt = statusData.lastSyncAt || null;

            if (statusData.status === 'completed') {
              this.snackBar.open('Sync completed successfully', 'Close', {
                duration: 2000,
              });
              this.fetchOrganisations(); // Refresh full list
            } else if (statusData.status === 'error') {
              this.snackBar.open(
                `Sync failed: ${statusData.message || 'Unknown error'}`,
                'Close',
                {
                  duration: 5000,
                }
              );
            } else if (statusData.status === 'syncing') {
              // Continue polling
              this.pollSyncStatus(org, attempts + 1);
            }
          },
          error: (err: HttpErrorResponse) => {
            this.snackBar.open('Failed to check sync status', 'Close', {
              duration: 3000,
            });
          },
        });
    }, 500); // Poll every 500ms
  }

  isSyncing(org: Organisation): boolean {
    return org.syncStatus === 'syncing';
  }
}
